<script setup lang="ts">
import RcShipmentSelect from '@/components/rc/common/RcShipmentSelect.vue'
import { useContainers } from '@/composables/rc/containers'
import type { ShipmentType } from '@/types/rc/shipments'
import { ref } from 'vue'

const props = defineProps<{
  containers: { id: number; number: string }[]
  workOrderNumber?: string
  customer?: number
  containerShipmentType: ShipmentType
}>()

const open = defineModel<boolean>()
const isLoading = ref<boolean>(false)
const emit = defineEmits(['containersAssigned'])

// Shipment select refs
const selectedShipmentID = ref<{ id: number }>()

const { assignContainersToShipmentMutation } = useContainers()

const assignContainers = async (shipmentID: number, containerIDs: number[]) => {
  assignContainersToShipmentMutation.mutate(
    {
      shipmentID: shipmentID,
      containerIDs: containerIDs,
    },
    {
      onSuccess: () => {
        open.value = false
        emit('containersAssigned')
      },
      onError: () => {
        isLoading.value = false
      },
    }
  )
}

/**
 * Handles form submit.
 *
 * @param formEl form element ref.
 */
const submit = async () => {
  if (!selectedShipmentID.value) return
  assignContainers(
    selectedShipmentID.value.id,
    props.containers.map((container) => container.id)
  )
}

/**
 * Reset form, reset mutations, emit closed
 */
const handleClosed = () => {
  isLoading.value = false
  selectedShipmentID.value = undefined
}
</script>

<template>
  <el-dialog v-model="open" :width="600" title="Assign to Shipment" :show-close="false" @closed="handleClosed">
    <p>You are assigning {{ containers.length }} container{{ containers.length > 1 ? 's' : '' }} to a shipment</p>
    <br />
    <div class="scrollable-list">
      <ul>
        <li v-for="cont in containers" :key="cont.id">{{ cont.number }}</li>
      </ul>
    </div>
    <br />
    <div class="flex flex-col gap-2">
      <p>Shipment</p>
      <RcShipmentSelect
        v-model="selectedShipmentID"
        :type="containerShipmentType"
        :work-order-number="workOrderNumber"
        :customer="customer"
        placeholder="Search for a shipment"
      />
    </div>
    <template #footer>
      <el-button @click="open = false">Cancel</el-button>
      <el-button type="primary" :loading="isLoading" :disabled="!selectedShipmentID" @click="submit()"
        >Assign</el-button
      >
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.scrollable-list {
  max-height: 132px;
  overflow-y: auto;
}

ul {
  list-style-type: disc;
  list-style-position: inside;
}
</style>
